import React, { useEffect, useMemo, useState } from "react";
import { Star } from "lucide-react";
import { fetchReviews, submitReview } from "../lib/api";
import { toast } from "sonner";

const Stars = ({ value, size = "h-3 w-3", onSelect }) => {
    return (
        <div className="inline-flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => {
                const filled = n <= value;
                const Tag = onSelect ? "button" : "span";
                return (
                    <Tag
                        key={n}
                        type={onSelect ? "button" : undefined}
                        onClick={onSelect ? () => onSelect(n) : undefined}
                        data-testid={onSelect ? `star-${n}` : undefined}
                        aria-label={onSelect ? `${n} stars` : undefined}
                        className={onSelect ? "p-0.5 hover:scale-110 transition-transform" : ""}
                    >
                        <Star
                            className={`${size} ${filled ? "fill-[#EDEDE6] text-[#EDEDE6]" : "text-[#444]"}`}
                            strokeWidth={1.2}
                        />
                    </Tag>
                );
            })}
        </div>
    );
};

export const Reviews = ({ slug }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", body: "" });
    const [submitting, setSubmitting] = useState(false);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        setLoading(true);
        fetchReviews(slug).then(setItems).finally(() => setLoading(false));
    }, [slug]);

    const average = useMemo(() => {
        if (items.length === 0) return 0;
        const sum = items.reduce((s, r) => s + r.rating, 0);
        return sum / items.length;
    }, [items]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.title || !form.body) {
            toast.error("Fill all fields before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            const created = await submitReview(slug, form);
            if (created.approved) {
                // Verified buyer — show instantly
                setItems((prev) => [created, ...prev]);
                toast.success("Verified — your review is live. Thank you.");
            } else {
                // Open submission — awaits moderation
                toast.success("Thank you — your review will appear after a brief check.");
            }
            setForm({ name: "", email: "", rating: 5, title: "", body: "" });
            setShowForm(false);
        } catch (err) {
            toast.error("Could not submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section data-testid="reviews-section" className="container-rail py-20 border-t border-white/5">
            <div className="grid grid-cols-12 gap-10">
                <div className="col-span-12 md:col-span-4">
                    <div className="text-overline text-[#A3A3A3] mb-3">Worn & Reported</div>
                    <h2 className="text-display text-4xl sm:text-5xl leading-[0.95]">From the people who wear it.</h2>
                    {items.length > 0 && (
                        <div className="mt-5 flex items-center gap-3" data-testid="reviews-average">
                            <Stars value={Math.round(average)} size="h-4 w-4" />
                            <span className="text-brutal text-sm">{average.toFixed(1)}</span>
                            <span className="text-overline text-[#A3A3A3]">{items.length} {items.length === 1 ? "review" : "reviews"}</span>
                        </div>
                    )}
                    <button
                        onClick={() => setShowForm((v) => !v)}
                        data-testid="review-toggle-form"
                        className="btn-ghost mt-8"
                    >
                        {showForm ? "Cancel" : "Write a review"}
                    </button>
                </div>

                <div className="col-span-12 md:col-span-8 space-y-6">
                    {showForm && (
                        <form onSubmit={onSubmit} data-testid="review-form" className="border border-white/10 p-6 space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="Your name"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid="review-name"
                                    className="bg-transparent border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-white/40"
                                />
                                <input
                                    type="email"
                                    placeholder="Email (verified buyers auto-publish)"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid="review-email"
                                    className="bg-transparent border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-white/40"
                                />
                            </div>
                            <div className="flex items-center gap-3 px-3 py-2 border border-white/10 w-fit">
                                <span className="text-overline text-[#A3A3A3]">Rating</span>
                                <Stars value={form.rating} size="h-4 w-4" onSelect={(n) => setForm({ ...form, rating: n })} />
                            </div>
                            <input
                                type="text"
                                placeholder="A short title"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                data-testid="review-title"
                                className="w-full bg-transparent border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-white/40"
                            />
                            <textarea
                                placeholder="How does it wear?"
                                value={form.body}
                                onChange={(e) => setForm({ ...form, body: e.target.value })}
                                rows={4}
                                data-testid="review-body"
                                className="w-full bg-transparent border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-white/40 resize-none"
                            />
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="review-submit"
                                className="btn-primary disabled:opacity-60"
                            >
                                {submitting ? "Submitting…" : "Submit Review"}
                            </button>
                        </form>
                    )}

                    {loading ? (
                        <div className="text-overline text-[#666]">Loading notes…</div>
                    ) : items.length === 0 ? (
                        <p className="text-[#A3A3A3] italic">No reviews yet — be the first to write one.</p>
                    ) : (
                        <ul className="space-y-6">
                            {items.map((r) => (
                                <li key={r.id} data-testid={`review-${r.id}`} className="border-t border-white/10 pt-6">
                                    <div className="flex items-center justify-between gap-4 flex-wrap">
                                        <div className="flex items-center gap-3">
                                            <Stars value={r.rating} />
                                            <span className="text-brutal text-sm">{r.title}</span>
                                        </div>
                                        <div className="text-overline text-[#666]">
                                            {r.name} {r.verified && <span className="text-[#3A5C45]">· Verified</span>}
                                        </div>
                                    </div>
                                    <p className="text-[#D6D6CC] leading-relaxed mt-3 text-[15px]">{r.body}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </section>
    );
};
